import { useEffect, useRef } from 'react';

import { fetchMyPresence, heartbeatPresence, setListingAvailable } from '../../util/api';
import {
  PRESENCE_HEARTBEAT_MS,
  isPresenceAutoOffError,
  isPresenceRateLimited,
  isPresenceSessionIdle,
  presenceRetryAfterMs,
  presenceTickPlan,
  shouldRestorePresenceAfterHeartbeat,
} from '../../util/presence';
import {
  getPresenceIntentIds,
  getPresenceOfflineIntentIds,
  hasPresenceIntent,
  setPresenceIntent,
  subscribePresenceIntent,
} from '../../util/presenceIntent';
import { requestInAppNotificationRefresh } from '../../util/inAppNotifications';

/**
 * Optional heartbeats while this tab is open. Available is saved on the
 * Listening profile once the listener turns it on. Login and logout turn it
 * off, and the server turns it off after 2 hours with no heartbeat.
 * Backs off on 429 so we do not hammer Sharetribe.
 */
const PresenceHeartbeat = props => {
  const { enabled } = props;
  const lastVisibleRef = useRef(Date.now());
  const backoffUntilRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    const markVisible = () => {
      if (document.visibilityState === 'visible') {
        lastVisibleRef.current = Date.now();
      }
    };
    markVisible();

    const applyBackoff = error => {
      if (isPresenceRateLimited(error)) {
        backoffUntilRef.current = Date.now() + presenceRetryAfterMs(error);
        return true;
      }
      return false;
    };

    let cancelled = false;
    const tick = () => {
      if (cancelled || inFlightRef.current) {
        return;
      }
      if (Date.now() < backoffUntilRef.current) {
        return;
      }
      // Phone lock / background tab (Sam, Oct 26 2026): keep heartbeating while
      // the page is still running so a locked phone stays Available Now.
      inFlightRef.current = true;
      fetchMyPresence()
        .then(payload => {
          if (cancelled) {
            return undefined;
          }
          const listings = payload?.data?.listings || [];
          const serverOnlineIds = listings.filter(item => item.online).map(item => item.listingId);
          const idle = isPresenceSessionIdle({
            visibilityState: document.visibilityState,
            lastVisibleAt: lastVisibleRef.current,
          });
          const plan = presenceTickPlan({
            idle,
            serverOnlineIds,
            intendedIds: getPresenceIntentIds(),
            offlineIds: getPresenceOfflineIntentIds(),
          });
          if (plan.action === 'offline') {
            return plan.listingIds.reduce((chain, listingId) => {
              return chain.then(() => {
                if (cancelled || Date.now() < backoffUntilRef.current) {
                  return undefined;
                }
                return setListingAvailable(listingId, false, { reason: 'idle' })
                  .then(payload => {
                    if (payload?.data?.online === false) {
                      setPresenceIntent(listingId, false);
                    }
                  })
                  .catch(error => {
                    applyBackoff(error);
                  });
              });
            }, Promise.resolve());
          }
          return plan.listingIds.reduce((chain, listingId) => {
            return chain.then(() => {
              if (cancelled || Date.now() < backoffUntilRef.current) {
                return undefined;
              }
              const intended = hasPresenceIntent(listingId);
              return heartbeatPresence(listingId)
                .then(payload => {
                  if (payload?.data?.startNowAccepted > 0) {
                    requestInAppNotificationRefresh();
                  }
                })
                .catch(error => {
                  if (isPresenceAutoOffError(error)) {
                    // Turned off after 2 hours away: the listener turns it back on by hand.
                    setPresenceIntent(listingId, false);
                    requestInAppNotificationRefresh();
                    return undefined;
                  }
                  if (
                    shouldRestorePresenceAfterHeartbeat({
                      intended,
                      idle: false,
                      error,
                    })
                  ) {
                    return setListingAvailable(listingId, true).then(() => {
                      setPresenceIntent(listingId, true);
                    });
                  }
                  applyBackoff(error);
                  return undefined;
                })
                .catch(error => {
                  applyBackoff(error);
                });
            });
          }, Promise.resolve());
        })
        .catch(error => {
          applyBackoff(error);
        })
        .finally(() => {
          inFlightRef.current = false;
        });
    };

    const onVisibility = () => {
      markVisible();
      if (document.visibilityState === 'visible') {
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', tick);
    window.addEventListener('focus', tick);

    tick();
    const timer = window.setInterval(tick, PRESENCE_HEARTBEAT_MS);
    const unsubscribe = subscribePresenceIntent(() => {
      if (getPresenceIntentIds().length > 0) {
        lastVisibleRef.current = Date.now();
        tick();
      }
    });
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', tick);
      window.removeEventListener('focus', tick);
    };
  }, [enabled]);

  return null;
};

export default PresenceHeartbeat;
